import { AppointmentType } from '@/components/agenda/types';
import AvatarNotes from '@/components/common/avatar-notes/avatar-notes';
import CollapseMenu from '@/components/common/collapse-menu/collapse-menu';
import DialogTemplate from '@/components/common/dialog-template/dialog-template';
import InfoTwoLinesBlock from '@/components/common/info-two-lines-block/info-two-lines-block';
import InterventionsDemandsList from '@/components/interventions/interventions-demands-list/interventions-demands-list';
import useGetAvatar from '@/hooks/useGetAvatar';
import useGetAvatarProperties from '@/hooks/useGetAvatarProperties';
import useGetAvatars from '@/hooks/useGetAvatars';
import { composeUri } from '@/utils/compose';
import {
    AVATARS,
    CONTACT_EMAIL_AVATAR_PROPERTY,
    CONTACT_NAME_AVATAR_PROPERTY,
    CONTACT_PHONE_AVATAR_PROPERTY,
    SITES,
} from '@/utils/CONST';
import { toShortFormatDate } from '@/utils/date';
import { getAvatarPropertyValue } from '@/utils/getProperty';
import extractIdFromURI from '@/utils/id';
import { HomeWorkOutlined } from '@mui/icons-material';
import { Skeleton } from '@mui/material';
import Box from '@mui/material/Box';
import { nth } from 'lodash';
import moment from 'moment';
import { useTranslate } from 'react-admin';

interface Props {
    appointmentData: AppointmentType;
    open: boolean;
    onClose: () => void;
}

/** Diálogo de detalle al pulsar una cita en el Scheduler (plantilla de cita por defecto de DevExtreme). */
const AgendaItem = ({ appointmentData, open, onClose }: Props) => {
    const translate = useTranslate();
    const queryEnabled = open && !!appointmentData.avatarId;

    const { avatars, isLoading: isLoadingAvatar } = useGetAvatars({
        filters: {
            id: [appointmentData.avatarId],
            serialization_groups: ['read_avatar_current_node', 'read_node', 'read_parent', 'read_avatar_parent'],
        },
        enabled: queryEnabled,
    });

    const item = nth(avatars);

    const siteIdParam = appointmentData.siteId
        ? (extractIdFromURI(appointmentData.siteId) ?? appointmentData.siteId)
        : null;
    const siteUriId = siteIdParam ? composeUri(AVATARS, siteIdParam) : undefined;

    const { avatar: site, isLoading: isLoadingSite } = useGetAvatar({
        avatarUriId: queryEnabled && siteUriId ? siteUriId : undefined,
        queryKey: `${SITES}_${siteIdParam ?? ''}`,
        filters: siteUriId
            ? {
                  'serialization_groups[0]': 'read_avatar_root',
                  'serialization_groups[1]': 'read_avatar_parent',
              }
            : undefined,
    });

    const { properties, isLoading: isLoadingProperties } = useGetAvatarProperties({
        avatarId: siteUriId ?? '',
        propertiesNames: [CONTACT_NAME_AVATAR_PROPERTY, CONTACT_PHONE_AVATAR_PROPERTY, CONTACT_EMAIL_AVATAR_PROPERTY],
        enabled: queryEnabled && !!siteUriId,
    });

    const isLoading = isLoadingAvatar || isLoadingSite || isLoadingProperties;

    return (
        <DialogTemplate
            title={isLoading ? '' : site?.name}
            open={open}
            onClose={onClose}
            maxWidth='lg'
            unifiedBodyScroll
        >
            {isLoading ? (
                <Box
                    sx={{
                        display: 'grid',
                        gap: 2,
                        p: 2,
                    }}
                >
                    {[1, 2, 3, 4, 5, 6].map((i) => (
                        <Skeleton key={i} height={40} />
                    ))}
                </Box>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
                    {site && (
                        <CollapseMenu
                            defaultOpen
                            disableInternalScroll
                            dialogSection
                            icon={<HomeWorkOutlined />}
                            text={translate('intervention.list.agenda_site_information_label')}
                        >
                            <Box
                                sx={{
                                    display: 'grid',
                                    gap: 2,
                                }}
                            >
                                <InfoTwoLinesBlock
                                    title={translate('site.form.address')}
                                    subtitle={site?.addressText ?? ''}
                                    id={'address'}
                                />

                                <InfoTwoLinesBlock
                                    title={translate('site.form.city')}
                                    subtitle={site?.city ?? ''}
                                    id={'city'}
                                />

                                <InfoTwoLinesBlock
                                    title={translate('site.form.postalCode')}
                                    subtitle={site?.postalCode ?? ''}
                                    id={'city'}
                                />

                                <InfoTwoLinesBlock
                                    title={translate('site.form.country')}
                                    subtitle={site?.country ?? ''}
                                    id={'country'}
                                />

                                <InfoTwoLinesBlock
                                    title={translate('site.installation_date')}
                                    subtitle={
                                        site?.creationDate && moment(site.creationDate).isValid()
                                            ? toShortFormatDate(site.creationDate)
                                            : ''
                                    }
                                    id={'date'}
                                />

                                <InfoTwoLinesBlock
                                    title={translate('site.contact_info')}
                                    subtitle={(() => {
                                        const lines = [
                                            getAvatarPropertyValue(properties, CONTACT_NAME_AVATAR_PROPERTY),
                                            getAvatarPropertyValue(properties, CONTACT_PHONE_AVATAR_PROPERTY),
                                            getAvatarPropertyValue(properties, CONTACT_EMAIL_AVATAR_PROPERTY),
                                        ].filter((v) => v != null && String(v).trim() !== '') as string[];
                                        return lines.length > 0 ? lines : '---';
                                    })()}
                                    id={'contact_info'}
                                />
                            </Box>
                        </CollapseMenu>
                    )}
                    {item?.id && (
                        <>
                            <AvatarNotes
                                selectedAvatarUriId={item.id}
                                collapseMenuDisableInternalScroll
                                collapseMenuDialogSection
                            />
                            <InterventionsDemandsList
                                selectedAvatarUriId={item.id}
                                collapseMenuDisableInternalScroll
                                collapseMenuDialogSection
                                demandItemVariant='dialog'
                            />
                        </>
                    )}
                </Box>
            )}
        </DialogTemplate>
    );
};

export default AgendaItem;
